import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle, useNoIndex } from '@/hooks/usePageTitle';
import { Navbar } from '@/components/Navbar';
import { ActionLink } from '@/components/visual/PagePrimitives';
import { LocaleLink } from '@/components/LocaleLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, Clock, Users, DollarSign, Download, Shield, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { CURRENCIES } from '@/lib/calculationEngine';
import { AssumptionsPanel } from '@/components/trust/AssumptionsPanel';
import { generateEstimatePDF } from '@/lib/pdfExport';
import { toast } from 'sonner';

const API = '/api';

function getCurrencySymbol(config) {
  return CURRENCIES.find(c => c.value === config?.currency)?.symbol || '$';
}

export default function SharedEstimatePage() {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  usePageTitle(data ? t('shared.pageTitle', { name: data.name }) : t('shared.pageTitleFallback'));
  useNoIndex();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/shared/${token}`);
        setData(res.data);
      } catch {
        setError(t('shared.notFoundHint'));
      } finally {
        setLoading(false);
      }
    })();
  }, [token, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6F7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#78B928] animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="product-ui board-view">
        <Navbar variant="light" />
        <div className="max-w-lg mx-auto px-6 py-20 text-center">
          <AlertTriangle className="w-12 h-12 text-[#B7791F] mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2" data-testid="shared-error-heading">{t('shared.notFound')}</h1>
          <p className="text-sm text-[#8A929B] mb-6">{error}</p>
            <ActionLink to="/" data-testid="shared-go-home">
              {t('shared.goHome')}
            </ActionLink>
        </div>
      </div>
    );
  }

  const { name, config, results, shared_by, created_at, tco } = data;
  const sym = getCurrencySymbol(config);
  const formatCost = (val) => `${sym}${(val || 0).toLocaleString()}`;

  return (
    <div className="product-ui board-view">
      <Navbar variant="light" />
      <main className="site-container disclosure-width board-main">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#527F19]/10 text-[#527F19] text-xs font-medium mb-3">
            <Shield className="w-3.5 h-3.5" /> {t('shared.badge')}
          </div>
          <h1
            className="text-2xl font-semibold"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            data-testid="shared-estimate-name"
          >
            {name}
          </h1>
          <div className="flex flex-wrap gap-x-4 mt-2 text-sm text-[#8A929B]">
            {shared_by && <span>{t('shared.sharedBy', { name: shared_by })}</span>}
            <span>{config?.industry ? t(`calcData.industries.${config.industry}`) : '-'}</span>
            <span>{config?.companySize ? t(`calcData.sizes.${config.companySize}`) : '-'}</span>
            {created_at && (
              <span>
                {new Date(created_at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>
        </div>

        <section className="board-financial" data-testid="shared-financial-headline">
          <p className="financial-label">{t('homepage.preview.investment')}</p>
          <p className="financial-value" data-testid="shared-total-investment">{formatCost(results.totalCost)}</p>
          <p className="text-sm text-[#66707A]">{t('results.costRange')}: {formatCost(results.costRange?.low)} – {formatCost(results.costRange?.high)}</p>
        </section>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="bg-white border-[#DDE1E5]">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 text-xs text-[#66707A]"><TrendingUp className="w-3 h-3" /> {t('estimates.effortLabel')}</div>
              <div className="mt-1 text-2xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shared-effort">{results.totalEffort}</div>
              <div className="text-xs text-[#66707A]">{t('results.personDays')}</div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#DDE1E5]">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 text-xs text-[#66707A]"><DollarSign className="w-3 h-3" /> {t('results.costRange')}</div>
              <div className="mt-1 text-lg font-bold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shared-cost">
                {formatCost(results.costRange?.low)} – {formatCost(results.costRange?.high)}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#DDE1E5]">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 text-xs text-[#66707A]"><Clock className="w-3 h-3" /> {t('estimates.duration')}</div>
              <div className="mt-1 text-2xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shared-duration">{results.totalDurationWeeks}</div>
              <div className="text-xs text-[#66707A]">{t('estimates.weeks')}</div>
            </CardContent>
          </Card>
          <Card className="bg-white border-[#DDE1E5]">
            <CardContent className="p-4">
              <div className="flex items-center gap-1 text-xs text-[#66707A]"><Users className="w-3 h-3" /> {t('estimates.fteUnit')}</div>
              <div className="mt-1 text-2xl font-semibold text-[#2867B2]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shared-fte">{results.fteRequired}</div>
              <div className="text-xs text-[#66707A]">{t('shared.required')}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="bg-white border-[#DDE1E5] mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">{t('shared.resourceAllocation')}</CardTitle>
          </CardHeader>          <CardContent>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
                <div className="text-xs text-[#66707A]">{t('estimates.internalDays')}</div>
                <div className="text-xl font-bold text-[#527F19]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{results.internalDays}</div>
              </div>
              <div className="p-3 rounded-lg bg-[#F5F6F7] border border-[#DDE1E5]">
                <div className="text-xs text-[#66707A]">{t('estimates.consultantDays')}</div>
                <div className="text-xl font-bold text-[#78B928]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{results.consultantDays}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-[#DDE1E5] mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">{t('results.costBreakdown')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { id: 'labor', label: t('advanced.labor'), value: results.costBreakdown?.labor, color: '#171A1D' },
              { id: 'audit', label: t('advanced.audit'), value: results.costBreakdown?.audit, color: '#2867B2' },
              { id: 'training', label: t('advanced.training'), value: results.costBreakdown?.training, color: '#527F19' },
              { id: 'tools', label: t('advanced.tools'), value: results.costBreakdown?.tools, color: '#8A929B' },
            ].map(item => {
              const total = Object.values(results.costBreakdown || {}).reduce((a, b) => a + b, 0);
              const pct = total ? Math.round((item.value / total) * 100) : 0;
              return (
                <div key={item.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#171A1D]">{item.label}</span>
                    <span className="text-[#8A929B]">{formatCost(item.value)} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-[#F5F6F7] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: item.color }} />
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {results.phases && results.phases.length > 0 && (
          <Card className="bg-white border-[#DDE1E5] mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">{t('results.projectPhases')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {results.phases.map(phase => (
                <div key={phase.id}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[#171A1D]">{t(`calcData.phases.${phase.id}`)}</span>
                    <span className="text-[#8A929B]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {phase.effort} {t('estimates.days')} / {phase.durationWeeks} {t('estimates.weeks')}
                    </span>
                  </div>
                  <Progress aria-label={t('calcData.phases.' + phase.id)} data-testid={`shared-phase-${phase.id}-progress`} value={phase.percentOfEffort} className="h-2" />
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <div className="mb-6">
          <AssumptionsPanel assumptions={results.assumptions} formatCost={formatCost} />
        </div>

        {tco?.years?.length > 0 && (
          <Card className="bg-white border-[#DDE1E5] mb-6" data-testid="shared-tco">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-[#66707A] uppercase tracking-wider">{t('tco.title')}</CardTitle>
              <p className="text-xs text-[#8A929B] normal-case tracking-normal">{t('tco.note')}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-3">
                {tco.years.map(year => (
                  <div key={year.year} className="border border-[#DDE1E5] rounded-lg p-3" data-testid={`shared-tco-year-${year.year}`}>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-[#8A929B]"
                         style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {t('tco.yearLabel', { year: year.year })}
                    </div>
                    <div className="mt-1 text-lg font-semibold text-[#171A1D]"
                         style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {formatCost(year.total)}
                    </div>
                    <div className="text-xs text-[#66707A]">
                      {year.phase === 'implementation' ? t('tco.phaseImplementation') : t('tco.phaseOperation')}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-baseline justify-between gap-3 border-t border-[#DDE1E5] pt-3">
                <span className="text-sm font-medium text-[#3A4249]">{t('tco.total')}</span>
                <span className="text-lg font-semibold text-[#171A1D]"
                      style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="shared-tco-total">
                  {formatCost(tco.total)}
                </span>
              </div>
              <p className="text-xs text-[#8A929B]" data-testid="shared-tco-recert">
                {t('tco.recertTitle')}: {t('tco.recertExcluded')}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            onClick={() => {
              generateEstimatePDF({ name, config, results, createdAt: created_at, tco, t });
              toast.success(t('estimates.pdfDownloaded'));
            }}
            className="bg-[#527F19] hover:bg-[#3F6114] text-white"
            data-testid="shared-download-pdf"
          >
            <Download className="w-4 h-4 mr-2" /> {t('shared.downloadPdf')}
          </Button>
            <ActionLink to="/auth" variant="outline" data-testid="shared-create-own">
              {t('shared.createOwn')}
            </ActionLink>
        </div>

        <p className="text-xs text-[#3A4249] italic" data-testid="shared-disclaimer">
          {t('shared.disclaimer')}
        </p>
      </main>
    </div>
  );
}
